import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Lock, Loader2, CheckCircle2 } from "lucide-react";

const formatErr = (detail) => {
  if (!detail) return "Something went wrong. Try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
};

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setErr("This reset link is missing its token. Request a new one.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (password.length < 8) return setErr("Password must be at least 8 characters.");
    if (password !== confirm) return setErr("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password reset. You're signed in.");
      await checkAuth();
      setTimeout(() => navigate("/studio", { replace: true }), 1400);
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-3xl bg-[#120E15] border border-[#F2C94C]/25 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-[#F2C94C] via-[#FF6B4A] to-[#00E5FF]" />
        <div className="p-8">
          <div className="w-14 h-14 mb-6 rounded-2xl bg-gradient-to-br from-[#F2C94C] to-[#FF6B4A] flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-[#0A0A0A]" />
          </div>
          <div className="font-mono-hy text-[10px] text-[#F2C94C] mb-2 tracking-wider">GENEO AI · PASSWORD RESET</div>
          <h1 className="font-display text-3xl text-white mb-2">Set a new password</h1>
          <p className="font-body text-sm text-white mb-6">
            Choose a strong password. You'll be signed in as soon as it's saved.
          </p>

          {done ? (
            <div data-testid="reset-success" className="rounded-2xl border border-[#00E5FF]/30 bg-[#00E5FF]/10 p-5 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#00E5FF] flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-mono-hy text-[10px] text-[#00E5FF] mb-1">DONE</div>
                <p className="font-body text-sm text-white">Password updated. Redirecting you to the Studio…</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
              <div>
                <label className="font-mono-hy text-[10px] text-white mb-1 block">NEW PASSWORD</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white" />
                  <input
                    type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-input-password" autoComplete="new-password" minLength={8} required
                    className="w-full pl-10 pr-3 py-3 rounded-xl bg-[#0A0A0A] border border-[#F2C94C]/15 text-white font-body text-sm focus:border-[#F2C94C]/60 focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="font-mono-hy text-[10px] text-white mb-1 block">CONFIRM PASSWORD</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white" />
                  <input
                    type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-input-confirm" autoComplete="new-password" minLength={8} required
                    className="w-full pl-10 pr-3 py-3 rounded-xl bg-[#0A0A0A] border border-[#F2C94C]/15 text-white font-body text-sm focus:border-[#F2C94C]/60 focus:outline-none"
                  />
                </div>
              </div>
              {err && (
                <div data-testid="reset-error" className="rounded-lg border border-[#EB5757]/40 bg-[#EB5757]/10 px-3 py-2 font-body text-xs text-[#EB5757]">
                  {err}
                </div>
              )}
              <button
                type="submit" disabled={busy || !token}
                data-testid="reset-submit"
                className="w-full font-mono-hy text-xs px-6 py-4 rounded-2xl bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save new password
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
